/**
 * Sign in, or make an account.
 *
 * One form with a toggle rather than two screens: the fields are the same, and the
 * difference is which route it posts to. The API signs a new account in as part of
 * registering, so both paths end the same way - a session cookie and a user.
 */

import { useState } from 'react';
import { ApiError, type Api } from '../api/http.js';
import type { Meta, User } from '../api/types.js';
import { Banner } from '../components/Banner.js';
import { InstallCard } from '../components/InstallCard.js';

export interface SignInScreenProps {
  api: Api;
  meta: Meta | null;
  onSignedIn: (user: User) => void;
}

export function SignInScreen({ api, meta, onSignedIn }: SignInScreenProps) {
  const [creating, setCreating] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const user = creating
        ? await api.register({ email, password, ...(displayName === '' ? {} : { displayName }) })
        : await api.signIn({ email, password });
      onSignedIn(user);
    } catch (failure) {
      setError(
        failure instanceof ApiError ? failure.message : failure instanceof Error ? failure.message : 'something went wrong',
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="signin">
      <header>
        <h1 translate="no" className="wordmark">Unsorted</h1>
        <p className="lede">
          Scan cards into any bin. The software remembers where they went, so pulling a deck is a walk down one short
          list instead of a search through the collection.
        </p>
      </header>

      {meta && !meta.recognition.available && (
        <Banner kind="warning">
          This server cannot recognise a capture yet. {meta.recognition.missing.join(' ')}
        </Banner>
      )}

      <form id="signin-form" onSubmit={(event) => void submit(event)} className="card">
        <h2>{creating ? 'Create an account' : 'Sign in'}</h2>
        {error && <Banner kind="error">{error}</Banner>}

        <label>
          Email
          <input
            type="email"
            name="email"
            autoComplete="username"
            inputMode="email"
            spellCheck={false}
            autoCapitalize="none"
            autoCorrect="off"
            placeholder="you@example.com…"
            required
            value={email}
            onChange={(event) => setEmail(event.target.value)}
          />
        </label>

        {creating && (
          <label>
            Display name
            <input
              type="text"
              name="displayName"
              autoComplete="nickname"
              autoCapitalize="words"
              placeholder="What should the app call you?…"
              value={displayName}
              onChange={(event) => setDisplayName(event.target.value)}
            />
          </label>
        )}

        <label>
          Password
          <input
            type="password"
            name="password"
            autoComplete={creating ? 'new-password' : 'current-password'}
            spellCheck={false}
            minLength={creating ? 10 : 1}
            placeholder={creating ? 'At least 10 characters…' : 'Your password…'}
            required
            value={password}
            onChange={(event) => setPassword(event.target.value)}
          />
        </label>
        {creating && <p className="hint">At least 10 characters. It is hashed with scrypt on the server.</p>}

        <button type="submit" className="primary" disabled={busy}>
          {busy ? 'Working…' : creating ? 'Create account and sign in' : 'Sign in'}
        </button>

        <button
          type="button"
          className="ghost"
          onClick={() => {
            setCreating((value) => !value);
            setError(null);
          }}
        >
          {creating ? 'I already have an account' : 'Create an account instead'}
        </button>
      </form>

      <InstallCard />

      <footer>
        <p>
          Card data and images from <a href="https://scryfall.com">Scryfall</a>. Magic: The Gathering is a trademark of
          Wizards of the Coast. This is unofficial Fan Content and is not endorsed by Wizards.
        </p>
      </footer>
    </div>
  );
}
